/**
 * Iconed KPI tile strip for the payroll surfaces — mirrors the prototype's
 * `.tiles` block (icon chip + uppercase key + mono value). The runs-list uses
 * the 4-up variant; the run-detail uses the 5-up variant with a highlighted
 * "net" tile.
 *
 * @see prototype/payroll/payroll-runs.html .tiles-4
 * @see prototype/payroll/payroll-run.html .tiles
 */
import type { LucideIcon } from "lucide-react";
import { cn } from "@flint/ui";

export interface KpiTile {
  icon: LucideIcon;
  label: string;
  value: string;
  /** Highlight as the sky "net payable" tile. */
  net?: boolean;
  /** Render the value smaller (a date / account, not a number). */
  text?: boolean;
}

export function PayrollKpiTiles({ tiles }: { tiles: KpiTile[] }) {
  return (
    <div
      className={cn(
        "grid gap-3",
        tiles.length === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-5",
      )}
    >
      {tiles.map((t) => (
        <div
          key={t.label}
          className={cn(
            "flex items-start gap-3 rounded-md border p-4 shadow-sm",
            t.net ? "border-sky-100 bg-sky-50" : "border-line bg-paper",
          )}
        >
          <span
            className={cn(
              "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
              t.net ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700",
            )}
          >
            <t.icon size={18} aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">
              {t.label}
            </div>
            <div
              className={cn(
                "mt-1 truncate font-mono font-semibold tracking-tight",
                t.text ? "text-sm text-ink-2" : "text-lg",
                t.net && !t.text ? "text-sky-700" : "text-ink",
              )}
            >
              {t.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
